import React, { CSSProperties } from 'react';
import {
  Divider,
  Grid2,
  Stack,
  SxProps,
  Theme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { PropsOf } from '@emotion/react';
import { Form, Formik, FormikProps, FormikValues } from 'formik';
import { LoadingButton } from '@mui/lab';
import SingleSelect from '../../form/base/SingleSelect';
import ButtonsSelect from '../../form/base/ButtonsSelect';
import StepsFlowStep from './StepsFlowStep';
import TextInput from '../../form/base/TextInput';
import CategorySingleSelect from '../../form/base/CategorySingleSelect';
import { FormikForm } from '../../../interfaces';
import TextareaInput from '../../form/base/TextareaInput';
import ValuesListener from '../../form/ValuesListener';

export type StepFlowCopyProps<Values extends FormikValues = any> = {
  name: keyof Values;
  contentType: 'copy';
  getCopyText: (values: Values) => string;
  getInstructions?: (values: Values) => React.ReactNode[];
  disabled?: boolean;
  onCopy?: () => void | Promise<void>;
};

export type StepInput<Values extends FormikValues = any> = {
  isOptional?: boolean;
} & (
  | {
      contentType: 'invisible';
      name: string;
    }
  | ({
      contentType: 'select';
    } & PropsOf<typeof SingleSelect>)
  | ({ contentType: 'category-select' } & PropsOf<typeof CategorySingleSelect>)
  | ({
      contentType: 'text';
    } & PropsOf<typeof TextInput>)
  | ({
      contentType: 'textarea';
    } & PropsOf<typeof TextareaInput>)
  | ({
      contentType: 'button-select';
    } & PropsOf<typeof ButtonsSelect>)
  | StepFlowCopyProps<Values>
);

export type Step<Values extends FormikValues = any> = {
  title: string;
  description: string | React.ReactNode;
  visitedDescription?: string | React.ReactNode;
  selectButtonText?: string;
  buttonText?: string;
  disabled?: boolean;
  isSubmit?: boolean;
  inputsGap?: CSSProperties['gap'];
  isHidden?: boolean;
  isError?: boolean;
  onNavigate?: (values: Values) => void | Promise<void>;
  isDefaultVisited?: boolean;
  inputs: StepInput[];
};

export type StepFormData<Values extends FormikValues = any> =
  FormikForm<Values> & {
    submitText?: string;
    steps:
      | Step<Values>[]
      | ((values: Values, helpers: FormikProps<Values>) => Step<Values>[]);
  };

interface Props<Values extends FormikValues = any> {
  isSequential: boolean;
  // if not sequential, require first step to be completed before jumping around
  isFirstStepRequired?: boolean;
  form: StepFormData<Values>;
  rightContent: (values: Values) => React.ReactNode;
  setValuesRef?: React.MutableRefObject<Values>;
  valuesRef?: React.MutableRefObject<Values | null>;
  submitRef?: React.MutableRefObject<() => void>;
  validateRef?: React.MutableRefObject<(values?: Values) => void>;
  submitButton?: {
    text: string;
    disabled?: boolean;
  };
  secondaryButton?: {
    text: string;
    onClick: () => void;
    disabled?: boolean;
  };
  defaultIsFirstStepComplete?: boolean;
  onValuesChange?: (values: Values) => void;
}

const BREAKPOINT = 'md';

const BUTTON_STYLE: SxProps<Theme> = {
  borderRadius: '5px',
  height: {
    xs: '50px',
    [BREAKPOINT]: 'auto',
  },
  fontSize: '1rem',
  fontWeight: 'bold',
  width: '100%',
  whiteSpace: 'nowrap',
};

function StepsFlow({
  form,
  isSequential,
  isFirstStepRequired,
  defaultIsFirstStepComplete,
  rightContent,
  setValuesRef,
  submitRef,
  valuesRef,
  validateRef,
  submitButton,
  secondaryButton,
  onValuesChange,
}: Props) {
  const isMedium = useMediaQuery<Theme>(theme =>
    theme.breakpoints.up(BREAKPOINT),
  );
  const theme = useTheme();
  const [currentStep, setCurrentStep] = React.useState(0);
  const [visitedSteps, setVisitedSteps] = React.useState<Set<number>>(
    new Set(),
  );

  React.useEffect(() => {
    setVisitedSteps(prev => {
      const newSet = new Set(prev);
      newSet.add(currentStep);
      return newSet;
    });
  }, [currentStep]);

  const isFirstStepComplete =
    defaultIsFirstStepComplete ||
    Array.from(visitedSteps).some(stepIdx => stepIdx > 0);

  const getSteps = React.useCallback(
    (values: FormikValues, helpers: FormikProps<FormikValues>) => {
      const vals = isSequential
        ? values
        : isFirstStepRequired && !isFirstStepComplete
        ? form.initialValues
        : values;
      const formSteps =
        typeof form.steps === 'function'
          ? form.steps(vals, helpers)
          : form.steps;

      return formSteps.filter(step => !step.isHidden);
    },
    [form, isFirstStepComplete, isFirstStepRequired, isSequential],
  );

  return (
    <Formik
      validateOnMount
      validateOnChange
      enableReinitialize
      initialValues={form.initialValues}
      validationSchema={form.validationSchema}
      onSubmit={form.onSubmit}
      innerRef={el => {
        if (el?.setValues && setValuesRef) {
          // eslint-disable-next-line no-param-reassign
          setValuesRef.current = el.setValues;
        }
        if (el?.submitForm && submitRef) {
          // eslint-disable-next-line no-param-reassign
          submitRef.current = el.submitForm;
        }
        if (el?.values && valuesRef) {
          // eslint-disable-next-line no-param-reassign
          valuesRef.current = el.values;
        }
        if (el?.validateForm && validateRef) {
          // eslint-disable-next-line no-param-reassign
          validateRef.current = el.validateForm;
        }
      }}>
      {helpers => {
        const formSteps = getSteps(helpers.values, helpers);
        return (
          <>
            <Form
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                }
              }}
              style={{
                width: '100%',
                padding: 0,
                boxSizing: 'border-box',
              }}>
              <Grid2
                container
                width="100%"
                overflow="hidden"
                columns={11}
                gap={2.5}
                wrap={isMedium ? 'nowrap' : 'wrap'}
                my={{ xs: 0, [BREAKPOINT]: 5 }}
                pb={{
                  xs: '75px',
                  [BREAKPOINT]: 0,
                }}>
                <Grid2
                  size={{
                    xs: 11,
                    [BREAKPOINT]: 6,
                  }}
                  overflow="hidden">
                  <Stack
                    divider={
                      <Divider
                        color={theme.palette.lightGrey.main}
                        variant="fullWidth"
                        component="div"
                        sx={{
                          borderColor: theme.palette.lightGrey.main,
                          p: 0,
                          m: 0,
                        }}
                      />
                    }
                    boxSizing="border-box"
                    width="100%"
                    border={`1px solid ${theme.palette.lightGrey.main}`}>
                    {formSteps?.map((step, index) => (
                      <StepsFlowStep
                        key={step.title.toString()}
                        currentStep={currentStep}
                        setCurrentStep={setCurrentStep}
                        isSequential={isSequential}
                        isFirstStepRequired={isFirstStepRequired}
                        isFirstStepComplete={isFirstStepComplete}
                        step={step}
                        stepIndex={index}
                        steps={formSteps}
                        submitText={form.submitText}
                        isVisited={
                          visitedSteps.has(index) || !!step.isDefaultVisited
                        }
                      />
                    ))}
                  </Stack>
                </Grid2>
                <Grid2
                  size={{
                    xs: 11,
                    [BREAKPOINT]: 5,
                  }}
                  overflow="hidden">
                  {rightContent(helpers.values)}
                </Grid2>
              </Grid2>
              {(submitButton || secondaryButton) && (
                <Stack
                  boxSizing="border-box"
                  flexDirection="row"
                  position="fixed"
                  height={{
                    xs: '75px',
                    [BREAKPOINT]: 'auto',
                  }}
                  top={{
                    xs: 'unset',
                    [BREAKPOINT]: '20px',
                  }}
                  right={{ xs: 0, [BREAKPOINT]: '32px' }}
                  left={{ xs: 0, [BREAKPOINT]: 'unset' }}
                  bottom={{
                    xs: 0,
                    [BREAKPOINT]: 'unset',
                  }}
                  width={{
                    xs: '100%',
                    [BREAKPOINT]: 'auto',
                  }}
                  px={{
                    xs: 2,
                    [BREAKPOINT]: 0,
                  }}
                  zIndex="1302"
                  display="flex"
                  alignItems="center"
                  justifyContent={{
                    xs: 'space-between',
                    [BREAKPOINT]: 'flex-end',
                  }}
                  bgcolor={{
                    xs: 'white',
                    [BREAKPOINT]: 'transparent',
                  }}
                  gap={2}>
                  {secondaryButton && (
                    <LoadingButton
                      type="button"
                      variant={isMedium ? 'link' : 'light'}
                      onClick={secondaryButton.onClick}
                      sx={BUTTON_STYLE}>
                      <span>{secondaryButton.text}</span>
                    </LoadingButton>
                  )}
                  {submitButton && (
                    <LoadingButton
                      type="submit"
                      className="primary steps-flow-submit"
                      variant="contained"
                      disabled={
                        submitButton.disabled ||
                        helpers.isSubmitting ||
                        !helpers.isValid
                      }
                      loading={helpers.isSubmitting}
                      sx={BUTTON_STYLE}>
                      <span>{submitButton.text}</span>
                    </LoadingButton>
                  )}
                </Stack>
              )}
            </Form>
            <ValuesListener onChange={onValuesChange} />
          </>
        );
      }}
    </Formik>
  );
}

export default StepsFlow;
